import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { TooltipOptions } from "primereact/tooltip/tooltipoptions";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import TextWithTip from "../../../../components/TextWithTip";
import { formatNumberOneDecimal } from "../../shared/reportUtil";
import { AdminReportsResponse, ReportLevel } from "../adminReportsApiSlice";
import { reportSubLevelName } from "../adminReportsUtil";
import { HolisticAndTraitScoresRow } from "./adminPerformanceReportApiSlice";

export function HolisticAndTraitScoresTable(props: {
	className: string;
	level: ReportLevel;
	data: AdminReportsResponse<HolisticAndTraitScoresRow>;
	hideRows: boolean;
	setDistrictFilter: (districtId: number) => void;
	setSchoolFilter: (schoolId: number) => void;
}) {
	const hasData = props.data && props.data.rows.length > 0;

	const tooltipOptions: TooltipOptions = {
		position: "top",
	};

	function changeFilters(id: number) {
		if (props.level === ReportLevel.State) {
			props.setDistrictFilter(id);
		} else if (props.level === ReportLevel.District) {
			props.setSchoolFilter(id);
		}
	}

	const nameTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return (
			<a
				className={`${props.level === ReportLevel.School ? "" : "link"}`}
				onClick={() => changeFilters(rowData.id)}>
				{rowData.name}
			</a>
		);
	};

	const teacherTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return (
			<span>
				<TextWithTip uniqueName={`teachers-${rowData.id}`} text={rowData.teachers} />
			</span>
		);
	};

	const developmentTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageDevelopment);
	};

	const organizationTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageOrganization);
	};

	const styleTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageStyle);
	};

	const wordChoiceTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageWordChoice);
	};

	const sentenceStructureTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageSentenceStructure);
	};

	const conventionsTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageConventions);
	};

	const totalTemplate = (rowData: HolisticAndTraitScoresRow) => {
		return formatNumberOneDecimal(rowData.averageTotalScore);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column
					footer={`${props.level}-Wide`}
					footerClassName="font-medium !text-right"
					colSpan={props.level === ReportLevel.School ? 2 : 1}
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageDevelopment)}
					footerClassName="font-medium"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageOrganization)}
					footerClassName="font-medium"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageStyle)}
					footerClassName="font-medium"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageWordChoice)}
					footerClassName="font-medium"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageSentenceStructure)}
					footerClassName="font-medium"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageConventions)}
					footerClassName="font-medium"
				/>
				<Column
					footer={formatNumberOneDecimal(props.data.totals.averageTotalScore)}
					footerClassName="font-medium"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={props.hideRows ? [] : props.data.rows}
			dataKey="id"
			loading={props.data.rows == undefined}
			emptyMessage="No results found"
			pt={{
				// Targets the container of the "No results found" message for hiding it
				emptyMessage: {
					style: { display: !props.hideRows ? "table-row" : "none" },
				},
			}}
			stripedRows
			sortIcon={CustomSortIcon}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
			footerColumnGroup={hasData && footerGroup}>
			<Column
				header={reportSubLevelName(props.level)}
				body={nameTemplate}
				sortable
				sortField="name"></Column>
			{props.level === ReportLevel.School && (
				<Column
					header="Teacher(s)"
					body={teacherTemplate}
					className="!max-w-52"
					sortable
					sortField="teachers"></Column>
			)}
			<Column
				header="Dev"
				headerTooltip="Development of Ideas"
				headerTooltipOptions={tooltipOptions}
				body={developmentTemplate}
				sortable
				sortField="averageDevelopment"></Column>
			<Column
				header="Org"
				headerTooltip="Organization"
				headerTooltipOptions={tooltipOptions}
				body={organizationTemplate}
				sortable
				sortField="averageOrganization"></Column>
			<Column
				header="Style"
				headerTooltip="Style"
				headerTooltipOptions={tooltipOptions}
				body={styleTemplate}
				sortable
				sortField="averageStyle"></Column>
			<Column
				header="WC"
				headerTooltip="Word Choice"
				headerTooltipOptions={tooltipOptions}
				body={wordChoiceTemplate}
				sortable
				sortField="averageWordChoice"></Column>
			<Column
				header="Sent"
				headerTooltip="Sentence Fluency"
				headerTooltipOptions={tooltipOptions}
				body={sentenceStructureTemplate}
				sortable
				sortField="averageSentenceStructure"></Column>
			<Column
				header="Conv"
				headerTooltip="Conventions"
				headerTooltipOptions={tooltipOptions}
				body={conventionsTemplate}
				sortable
				sortField="averageConventions"></Column>
			<Column
				header="Total"
				body={totalTemplate}
				sortable
				sortField="averageTotalScore"></Column>
		</DataTable>
	);
}
